import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { useEffect } from 'react';
import { initGA, trackPageView } from './utils/analytics';

import ForumOverview from './pages/ForumOverview';
import ThreadList from './pages/ThreadList';
import PostList from './pages/PostList';
import Register from './pages/Register';
import Login from './pages/Login';
import Navigation from './components/Navbar';
import Dashboard from './pages/Dashboard';
import ForumManagement from './pages/ForumManagement';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import GroupManagement from './pages/GroupManagement';
import UserCP from './pages/UserCP';
import UnreadThreadsView from './pages/UnreadList';
import AdminCms from './pages/AdminCms';
import ContentPage from './pages/ContentPage';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import RecentThreadsView from './pages/RecentThreadsView';
import Search from './pages/Search';
import Footer from './components/Footer';
import AnalyticsNotice from './components/AnalyticsNotice';
import { isLocalStorageAvailable } from './utils/storageCheck';
import StorageErrorPage from './pages/StorageErrorPage';


import PmCenter from './pages/PmCenter';

function AnimatedRoutes() {
  const location = useLocation();

  // Track only specific page views we care about
  useEffect(() => {
    const path = location.pathname;
    
    // Only track main forum overview
    if (path === '/' || path === '/forum') {
      trackPageView('/forum-overview', 'Forum Übersicht');
    }
    // Thread and forum specific tracking is now handled in their respective components
  }, [location]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={wrapWithMotion(<ForumOverview />)} />
        <Route path="/forum" element={wrapWithMotion(<ForumOverview />)} />
        <Route path="/forum/:forumId" element={wrapWithMotion(<ThreadList />)} />
        <Route path="/thread/:threadId" element={wrapWithMotion(<PostList />)} />
        <Route path="/admin" element={wrapWithMotion(<Dashboard />)} />
        <Route path="/admin/forums" element={wrapWithMotion(<ForumManagement />)} />
        <Route path="/admin/users" element={wrapWithMotion(<UserManagement />)} />
        <Route path="/admin/settings" element={wrapWithMotion(<Settings />)} />
        <Route path="/admin/groups" element={wrapWithMotion(<GroupManagement />)} />
        <Route path="/register" element={wrapWithMotion(<Register />)} />
        <Route path="/login" element={wrapWithMotion(<Login />)} />
        <Route path="/usercp" element={wrapWithMotion(<UserCP />)} />
        <Route path="/admin/cms" element={wrapWithMotion(<AdminCms />)} />
        <Route path="/:slug" element={<ContentPage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/threads/recent" element={<RecentThreadsView />} />
<Route path="/search" element={<Search />} />
        {/* Admin-Route für CMS */}

        <Route path="/unread" element={wrapWithMotion(<UnreadThreadsView />)} />

        <Route path="/pm" element={wrapWithMotion(<PmCenter />)} />
      </Routes>
    </AnimatePresence>
  );
}

// Hilfsfunktion für saubere Animation
const wrapWithMotion = (component) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ 
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }}
    className="px-0 py-2 sm:p-6"
  >
    <div className="max-w-7xl mx-auto">
      {component}
    </div>
  </motion.div>
);

function App() {
  // Initialize Google Analytics
  useEffect(() => {
    initGA();
    
    // Listen for GA enablement changes
    const checkForGAEnabled = () => {
      if (window.GA_ENABLED && !window.GA_INITIALIZED) {
        initGA();
        window.GA_INITIALIZED = true;
      }
    };
    
    // Check periodically if GA gets enabled
    const interval = setInterval(checkForGAEnabled, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Weiche für deaktivierten localStorage
  if (!isLocalStorageAvailable()) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex items-center justify-center">
        <StorageErrorPage />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen" style={{ 
      background: 'linear-gradient(135deg, #000 0%, #448f65 100%)',
      backgroundAttachment: 'fixed'
    }}>
      {/* Content with proper z-index */}

      {/* Hauptinhalt */}
      <div className="relative z-10">
        <Router basename="/forum">
          <Navigation />
          <AnimatedRoutes />
          <Footer />
          <AnalyticsNotice />
          <ToastContainer 
            position="top-right" 
            autoClose={3000}
            className="mt-20"
            toastClassName="backdrop-blur-lg bg-white/90 text-gray-900 rounded-2xl shadow-glass"
            progressClassName="bg-primary"
          />
        </Router>
      </div>
    </div>
  );
}
export default App;
